import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import get from 'lodash.get';
import hash from 'object-hash';
import { Footer } from 'components/Footer';
import { LoadingOverlay } from 'components/LoadingOverlay';
import { useActions } from 'hooks/useActions';
import { selectors as cartSelectors, Cart } from 'services/cart';
import { selectors as configSelectors, Configuration, EventDisplaySettings, Messages } from 'services/configuration';
import { selectors as kioskSelectors, Kiosk } from 'services/kiosk';
import { selectors as mediaSelectors, MediaResponse } from 'services/media';
import {
  actions as ticketingActions,
  selectors as ticketingSelectors,
  EventDetails,
  Offer,
  Tier,
  TierSelection,
  Group,
  GroupEvent,
  UpsellStore
} from 'services/ticketing';
import { getEventDisplaySettings } from 'views/Ticketing/utils';
import PackageView from './components/PackageView';
import EventView from './components/EventView';

const TierSelectionView: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as { from?: string };

  // Action Creators and Selectors
  const { getEventDetails, setTiersConfig, resetEventDetails, resetUpsell } = useActions({
    getEventDetails: ticketingActions.getEventDetails,
    setTiersConfig: ticketingActions.setTiersConfig,
    resetEventDetails: ticketingActions.resetEventDetails,
    resetUpsell: ticketingActions.resetUpsell
  });
  const cart = useSelector(cartSelectors.selectCart) as Cart;
  const kiosk = useSelector(kioskSelectors.selectKiosk) as Kiosk;
  const configuration = useSelector(configSelectors.selectConfiguration) as Configuration;
  const event = useSelector(ticketingSelectors.selectEvent);
  const tiersConfig = useSelector(ticketingSelectors.selectTiersConfig) || { selected: '' };
  const eventDetails = useSelector(ticketingSelectors.selectEventDetails) as EventDetails;
  const eventImages = useSelector(mediaSelectors.selectEventImages) || [];
  const venueImages = useSelector(mediaSelectors.selectVenueImages) || [];
  const fetchCompleted = useSelector(ticketingSelectors.selectFetchCompleted);
  const ticketingPath = useSelector(ticketingSelectors.selectTicketingConfigPath);
  const upsell = useSelector(ticketingSelectors.selectUpsell) as UpsellStore;
  const upsellProducts = useSelector(ticketingSelectors.selectUpsellProducts);

  const touchForward = get(configuration, 'configuration.globalSettings.touchForward.purchaseFlow.enabled');
  const cartEnabled = get(configuration, `${ticketingPath}.cart.enabled`, false);
  const cartLabel = get(configuration, `configuration.labels.cart.${i18n.language}`, t('cart.cartLabel'));
  const productAvailabilityAndSelection = get(configuration, `${ticketingPath}.productAvailabilityAndSelection`);
  const upsellMessages: Messages = get(productAvailabilityAndSelection, `upsell.tierSelectionMessages`, {});
  const upsellBackText = get(upsellMessages, `buttons.${i18n.language}[0]`, t('commonText.back'));
  const upsellProceedText = get(
    upsellMessages,
    `buttons.${i18n.language}[1]`,
    cartEnabled ? t('cartSummary.proceedToCart', { cartLabel }) : t('commonText.next')
  );
  const eventDisplaySettingsList: EventDisplaySettings[] = get(
    configuration,
    `${ticketingPath}.eventDisplaySettingsList`,
    []
  );

  const [tierSelection, setTierSelection] = React.useState<Record<string, TierSelection>>(tiersConfig.selected || []);
  const [tiers, setTiers] = React.useState<Record<string, Tier>>({});
  const [groupEvents, setGroupEvents] = React.useState<Record<string, Group>>({});
  const [groupIndex, setGroupIndex] = React.useState<number>(-1);
  const [autoSelected, setAutoSelected] = React.useState<boolean>(false);
  const [direction, setDirection] = React.useState<string>('forward');
  const [isNextEnabled, setNextEnabled] = React.useState<boolean>(false);
  const [uiLoading, setUILoading] = React.useState(true);
  const [isTouchForward, setIsTouchForward] = React.useState(touchForward);
  const [skipAndContinue, setSkipAndContinue] = React.useState(false);
  const { clientId } = kiosk.venue;

  const { autoSelectTier } = getEventDisplaySettings(eventDisplaySettingsList, eventDetails ? eventDetails.name : '');

  const getTierOffers = (offers: Offer[]): Record<string, Tier> => {
    return offers
      .sort((a, b) => b.basePrice - a.basePrice)
      .sort((a, b) => a.tierPriority - b.tierPriority)
      .reduce<{ [x: string]: Tier }>((acc, cur) => {
      const { tier } = cur;
      if (acc.hasOwnProperty(tier)) {
        acc[tier].offers = acc[tier].offers.concat(cur);
        acc[tier].tierAvailability = cur.availability;
        acc[tier].minPrice = acc[tier].minPrice < cur.basePrice ? acc[tier].minPrice : cur.basePrice;
        acc[tier].maxPrice = acc[tier].maxPrice > cur.basePrice ? acc[tier].maxPrice : cur.basePrice;
        acc[tier].hasPromoOffer = acc[tier].offers.some((o) => o.isPromoCodeOffer);
      } else {
        acc[tier] = {
          offers: [cur],
          tierAvailability: cur.availability,
          minPrice: cur.basePrice,
          maxPrice: cur.basePrice,
          hasPromoOffer: cur.isPromoCodeOffer
        };
      }
      /*
         * If acc has a key value that is equal to the value on the key of tier,
         * spread the initial values into an array, then tack on a new object with
         * the cur.basePrice, cur.type, and rest of properties on the cur, onto the
         * acc[key].
         * Else create a next key on the acc object (acc[key]) that is equal to an array,
         * with an object that has the cur.basePrice, cur.type, and rest of the properties
         * on the cur.
         */
      return acc;
    }, {});
  };

  const getGroupEvents = () => {
    return Object.values(eventDetails.groups).reduce<{ [x: string]: Group }>((acc, cur) => {
      const { id } = cur;
      cur.events.forEach((eventData) => {
        const tierData = getTierOffers(eventData.offers);
        const groupEvent: GroupEvent = {
          id: eventData.id,
          name: eventData.name,
          availability: eventData.availability,
          tiers: tierData,
          groupId: cur.id,
          description: eventData.description,
          venue: eventData.venue,
          // only set the date if the package (event) has a date range
          date: event && event.firstDate && event.lastDate ? (eventData.date as string) : '',
          imageUrl: eventData.imageUrl
        };

        if (acc.hasOwnProperty(id)) {
          acc[id].events = acc[id].events.concat(groupEvent);
        } else {
          acc[id] = {
            groupId: cur.id,
            name: cur.name,
            maxEvents: cur.maxEvents,
            minEvents: cur.minEvents,
            events: [groupEvent]
          };
        }
      });
      return acc;
    }, {});
  };

  /** Set the Next button to Skip and/or enable it for the upsell flow */
  const manageUpsellNextBtn = (selection?: Record<string, TierSelection>) => {
    let selTier = '';
    if (selection) {
      selTier = get(selection.tier, 'tier');
    } else {
      selTier = get(tiersConfig, 'selected.tier.tier');
    }
    if (selTier) {
      setNextEnabled(true);
      setSkipAndContinue(false);
    } else if (eventDetails.upsell) {
      // if on the initial upsell product
      setNextEnabled(!eventDetails.upsell.initialProductRequired);
      setSkipAndContinue(!eventDetails.upsell.initialProductRequired);
    } else if (upsellProducts.length === 1 && cart) {
      // if on a single upsell product and a cart exists
      setNextEnabled(true);
      setSkipAndContinue(true);
    }
  };

  // On mount gets the event details needed for the page
  React.useEffect(() => {
    if (!eventDetails) getEventDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (eventDetails && fetchCompleted.eventDetails) {
      // Collects all the same tiers into one
      if (eventDetails.offers && eventDetails.offers.length) {
        setTiers(getTierOffers(eventDetails.offers));
        // Clear existing selection if touch forward enabled - Peformance flow
        if (isTouchForward && state && state.from === '/ticketing/selection') {
          setTierSelection({});
        }
        if (eventDetails.upsell || upsellProducts.length) {
          manageUpsellNextBtn();
        }
      } else if (eventDetails.groups && eventDetails.groups.length) {
        setGroupEvents(getGroupEvents());
      } else if (eventDetails.offers && !eventDetails.offers.length && upsellProducts.length === 1) {
        // If there is only one upsell product with no offers, skip this page
        loadTicketSelection();
      } else {
        setUILoading(false);
      }
    } else if (fetchCompleted.eventDetails) {
      setUILoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchCompleted.eventDetails]);

  // auto select tier if applicable
  React.useEffect(() => {
    if (tiers && Object.keys(tiers).length && fetchCompleted.eventDetails) {
      const availableTiers = Object.keys(tiers).filter((tierName) => tiers[tierName].tierAvailability !== 0);
      if (availableTiers.length > 0 && (autoSelectTier || availableTiers.length === 1)) {
        if (state && state.from === '/ticketing/selection') {
          onPrevPageClick();
        } else {
          setAutoSelected(true);
          updateTier(availableTiers[0]);
        }
      } else {
        setUILoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tiers, fetchCompleted.eventDetails]);

  // Fires if there is a package with groups
  React.useEffect(() => {
    if (groupEvents && Object.keys(groupEvents).length) {
      // Set the index for which group is showing
      if (state && state.from === '/ticketing/selection') {
        setDirection('backward');
        setGroupIndex(Object.keys(groupEvents).length - 1);
      } else {
        setGroupIndex(0);
      }
    } else if (fetchCompleted.eventDetails) {
      // Groups loaded without any offers
      setUILoading(false);
    }
  }, [groupEvents]);

  // Fires for each group of a package
  React.useEffect(() => {
    if (groupIndex > -1 && groupIndex < Object.keys(groupEvents).length) {
      const currentGroup = groupEvents[Object.keys(groupEvents)[groupIndex]];
      const currentTierSelection = { ...tierSelection };

      if (currentGroup.minEvents === currentGroup.maxEvents) {
        // Reset touchForward capability in case it was disabled for the previous group
        setIsTouchForward(touchForward);

        // Handle auto selecting tier(s) for a package group
        let autoSelectTierCounter = 0;
        if (currentGroup.minEvents === currentGroup.events.length) {
          // If all events are required, determine which events can have a tier auto selected
          currentGroup.events.map((oneEvent) => {
            const availableTiers = Object.keys(oneEvent.tiers).filter(
              (tierName) => oneEvent.tiers[tierName].tierAvailability !== 0
            );
            if (availableTiers.length > 0 && (autoSelectTier || availableTiers.length === 1)) {
              currentTierSelection[oneEvent.id] = {
                tier: availableTiers[0],
                groupId: currentGroup.groupId
              };
              autoSelectTierCounter++;
            }
          });
        }

        // Handle selection/navigation if all events have a tier to auto select
        if (autoSelectTierCounter === currentGroup.events.length) {
          if (direction === 'backward') {
            onPrevPageClick();
          } else {
            setAutoSelected(true);
            setTierSelection(currentTierSelection);
          }
        } else {
          // Handle when tiers are not auto selected for every event
          if (touchForward && direction === 'backward') {
            // Need to reset selections to allow touch forward to work
            resetTierSelectionForGroup();
          }
          setUILoading(false);
        }
      } else {
        // Disable touchForward if the group has a different # for minEvents and maxEvents
        setIsTouchForward(false);
        setUILoading(false);
      }
    }
  }, [groupIndex]);

  // Fires when a tier is selected/deselected, or the package group changed
  React.useEffect(() => {
    if (tiers && Object.keys(tiers).length) {
      // Handle behavior for an event: enable the Next button or navigate forward
      if (autoSelected) {
        onNextPageClick();
      } else if (tierSelection.tier) {
        setNextEnabled(true);
        // Advance to next page if touch forward enabled - Peformance flow
        if (isTouchForward) onNextPageClick();
      }
    } else if (groupIndex > -1 && Object.keys(groupEvents).length) {
      // Handle behavior for a package: enable the Next button if the min/max requirements are met
      const currentGroup = groupEvents[Object.keys(groupEvents)[groupIndex]];
      const selectedTierCount = Object.values(tierSelection).filter(
        (item) => item.groupId === currentGroup.groupId
      ).length;
      if (selectedTierCount >= currentGroup.minEvents && selectedTierCount <= currentGroup.maxEvents) {
        setNextEnabled(true);
      } else {
        setNextEnabled(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tierSelection, groupIndex]);

  // Handle package flow forward navigation - for isTouchForward & auto selected tiers
  React.useEffect(() => {
    if (isNextEnabled && groupIndex > -1 && direction !== 'backward' && (isTouchForward || autoSelected)) {
      onNextPageClick();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNextEnabled]);

  // ***************
  // ** FUNCTIONS **
  // ***************

  const getImageUrl = (eventName: string, venueName?: string, imageUrl?: string): string => {
    let imgObj =
      venueName &&
      venueImages.find((item: MediaResponse) => {
        return item.name === hash(venueName.replace(new RegExp('/', 'g'), ' '));
      });
    // venue image from media table
    if (imgObj && imgObj.s3Name) {
      const venuePreviewUrl = `${process.env.REACT_APP_S3_ASSETS}/${clientId}/venue_images/`;
      return venuePreviewUrl.concat(imgObj.s3Name);
    }
    imgObj =
      eventName &&
      eventImages.find((item: MediaResponse) => {
        return item.name === hash(eventName.replace(new RegExp('/', 'g'), ' '));
      });
    // event image from media table
    if (imgObj && imgObj.s3Name) {
      const eventPreviewUrl = `${process.env.REACT_APP_S3_ASSETS}/${clientId}/event_images/`;
      return eventPreviewUrl.concat(imgObj.s3Name);
    }
    // event image from ticketing system
    if (imageUrl) {
      return imageUrl;
    }

    return '';
  };

  const onPrevPageClick = () => {
    setDirection('backward');
    if (groupEvents && groupIndex > 0) {
      resetTierSelectionForGroup();
      setGroupIndex(groupIndex - 1);
    } else {
      // Reset tier selection and event details
      setTiersConfig({ selected: '' });
      resetEventDetails();

      // Reset upsell if on the initial product (details have not been saved yet)
      if (!upsell.initialProduct.details) resetUpsell();

      let prevPage = '/ticketing/events';
      if (upsell.initialProduct.details) {
        prevPage = '/ticketing/upsell-selection';
      }
      // re-routes to the previous page (EventsView)
      navigate(prevPage, { state: { from: location.pathname } });
    }
  };

  const onNextPageClick = () => {
    setDirection('forward');
    if (skipAndContinue) {
      loadTicketSelection();
    } else {
      if (groupIndex < Object.keys(groupEvents).length - 1) {
        setUILoading(true);
        setNextEnabled(false);
        setAutoSelected(false);
        setGroupIndex(groupIndex + 1);
      } else {
        // sets the tiers config to have the selected tier
        setTiersConfig({ selected: tierSelection });
        loadTicketSelection();
      }
    }
  };

  // Navigates to the next page (TicketSelectionView)
  const loadTicketSelection = () => {
    navigate('/ticketing/selection', { state: { from: location.pathname } });
  };

  // Remove all selected tiers for a package group
  const resetTierSelectionForGroup = () => {
    const currentTierSelection = { ...tierSelection };
    const currentGroup = groupEvents[Object.keys(groupEvents)[groupIndex]];

    currentGroup.events.forEach((e) => {
      if (currentTierSelection[e.id]) {
        delete currentTierSelection[e.id];
      }
    });

    setTierSelection(currentTierSelection);
  };

  const updateTier = (tier: string) => {
    const currentSelection = { ...tierSelection };
    // if already selected: deselect and disable next button
    if (
      currentSelection &&
      currentSelection.tier &&
      currentSelection.tier.tier &&
      currentSelection.tier.tier === tier
    ) {
      setNextEnabled(false);
      delete currentSelection.tier;
    } else {
      currentSelection.tier = { tier };
    }
    setTierSelection(currentSelection);
    if (eventDetails.upsell || upsellProducts.length) {
      manageUpsellNextBtn(currentSelection);
    } else {
      setSkipAndContinue(false);
    }
  };

  // list of selected tiers
  const updateGroupTier = (tier: string, id: string, date: string) => {
    // For handling the navigation within the package groups,
    // when a tier is selected, consider it as a forward flow.
    setDirection('forward');
    const currentSelection = { ...tierSelection };
    const { groupId, maxEvents } = groupEvents[Object.keys(groupEvents)[groupIndex]];
    let count = 1;
    if (currentSelection[id] && currentSelection[id].tier === tier) {
      delete currentSelection[id];
    } else {
      if (!!!currentSelection[id]) {
        count = Object.values(currentSelection).filter((item) => item.groupId === groupId).length;
        if (count === maxEvents) {
          const firstItem = Object.keys(currentSelection).find(
            (key) => currentSelection[key] && currentSelection[key].groupId === groupId
          );
          if (firstItem) delete currentSelection[firstItem];
        }
      }
      currentSelection[id] = { tier, groupId, date };
    }
    setTierSelection(currentSelection);
  };

  const getNextText = () => {
    let nextText;
    if (isTouchForward) {
      // 'Skip' if on the upsell initial product and it is not required
      if (eventDetails.upsell && !eventDetails.upsell.initialProductRequired) {
        nextText = t('commonText.skip');
        // 'Proceed to cart' if on a single upsell product
      } else if (!eventDetails.upsell && upsellProducts.length === 1 && cart) {
        nextText = upsellProceedText;
      }
      // otherwise it's undefined so the button is hidden
    } else if (skipAndContinue && !eventDetails.upsell && upsellProducts.length === 1 && cart) {
      nextText = upsellProceedText;
    } else if (skipAndContinue) {
      nextText = t('commonText.skip');
    } else {
      nextText = t('commonText.next');
    }
    return nextText;
  };

  // If there is no event redirect to the (GenresView)
  if (!event) return <Navigate to="/ticketing/genres" />;

  // If no eventDetails or if loading, show the loading overlay
  if (uiLoading) return <LoadingOverlay />;

  return (
    <>
      {tiers && Object.keys(tiers).length ? (
        <EventView
          event={eventDetails}
          tiers={tiers}
          tierSelection={tierSelection}
          getImageUrl={getImageUrl}
          updateTier={updateTier}
        />
      ) : (
        <PackageView
          groupIndex={groupIndex}
          groupEvents={groupEvents}
          tierSelection={tierSelection}
          getImageUrl={getImageUrl}
          updateGroupTier={updateGroupTier}
        />
      )}
      {/* Footer */}
      <Footer
        onPrevClick={onPrevPageClick}
        onNextClick={onNextPageClick}
        isNextDisabled={!isNextEnabled}
        prevText={upsellProducts.length > 0 ? upsellBackText : t('commonText.back')}
        nextText={getNextText()}
      />
    </>
  );
};

export default TierSelectionView;
